import React, { useMemo } from 'react';
import { Img, useVideoConfig } from 'remotion';

export type CropRect = {
    x: number;
    y: number;
    width: number;
    height: number;
};

export const CroppedImg: React.FC<{
    src: string;
    cropStart?: CropRect;
    cropEnd?: CropRect;
    progress: number;
    style?: React.CSSProperties;
    imageWidth?: number;
    imageHeight?: number;
}> = ({ src, cropStart, cropEnd, progress, style, imageWidth, imageHeight }) => {
    const { width: configWidth, height: configHeight } = useVideoConfig();

    // Interpolate function matches original
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

    const start = cropStart;
    const end = cropEnd || start;

    // Current interpolation state
    // If no cropStart provided, we might want a default full crop? 
    // The original code crashed or handled it upstream? 
    // Original: const start = cropStart; const end = cropEnd || start; 
    // If cropStart is undefined, start is undefined. 
    // Then start.x throws. 
    // We assume cropStart IS provided or handled by parent (as seen in ImageCarousel Logic).
    // But for safety let's default to full image 0,0,1,1 if missing?
    const defaultRect = { x: 0, y: 0, width: 1, height: 1 };
    const s = start || defaultRect;
    const e = end || s;

    const x = lerp(s.x, e.x, progress);
    const y = lerp(s.y, e.y, progress);
    const w = lerp(s.width, e.width, progress);
    const h = lerp(s.height, e.height, progress);

    const imgStyle = useMemo(() => {
        // New Logic from Elixir Reference
        // Container aspect ratio (Target Aspect)
        // We assume the component fills the container, but we don't know the container size in pixels usually.
        // However, we can use the ratio of Width/Height percentages or mix units?
        // Actually, best is to use the VIDEO config aspect ratio if we assume full screen, 
        // OR rely on the fact that we output percentages.

        // Elixir logic relies on comparing ARs.
        // We need 'target_aspect'. 
        // If this component is inside a smaller box, we might be wrong if we use video config.
        // BUT, CSS % works relative to parent.
        // We need AR comparison. 
        // Let's assume the PARENT container has the same AR as the Composition (common case)
        // OR we can't really do "Cover" logic correctly without knowing Container AR.
        // PROPOSAL: Use VideoConfig AR as default target AR.
        const targetAspect = configWidth / configHeight;
        const srcAspect = (imageWidth ?? 1) / (imageHeight ?? 1);

        // "The crop region's aspect ratio in the source image"
        // crop_aspect_rate = (width * src_aspect.w) / (height * src_aspect.h)
        // width, height are normalized (fraction of image).
        // crop_width_pixels = width * imageWidth
        // crop_height_pixels = height * imageHeight
        // crop_AR = crop_width_pixels / crop_height_pixels 
        //         = (width * imageWidth) / (height * imageHeight)
        //         = (width / height) * srcAspect
        const cropAspect = (w / h) * srcAspect;

        let style: React.CSSProperties = {
            position: 'absolute',
            objectFit: 'cover',
            objectPosition: '0 0',
            maxWidth: 'unset', // Important override
        };

        if (cropAspect > targetAspect) {
            // Crop is wider than target - scale to fit Height
            // Based on Elixir: h_scale = container_height / height_pixels?
            // "h = container_height / height" (where height is normalized?) No, context matters.

            // Re-derivation:
            // We want CurrentCropHeightPixels = ContainerHeightPixels.
            // CurrentCropHeightPixels = h * ImageHeightPixels.
            // ImageHeightPixels = scale * OriginalImageHeight ? No, we are setting CSS width/height of img.
            // Let H_css be the CSS height we apply to the image.
            // CurrentCropHeightCSS = h * H_css.
            // We want CurrentCropHeightCSS = 100% (of Container).
            // So h * H_css = 100%
            // H_css = 100 / h %

            style.height = `${100 / h}%`;
            style.width = 'auto'; // Maintain AR

            // And calculate Left/Top shift
            // Shift is still relative to the IMAGE dimensions.
            // left = -(x * image_width_css)
            // But we don't know image_width_css in pixels easily?
            // We know it in % relative to container?
            // If H_css = 100/h %, and W_css is auto...
            // W_css / H_css = srcAspect.
            // W_css = H_css * srcAspect = (100/h) * srcAspect % (relative to Height-of-Container?)
            // Wait, CSS % on 'width' is relative to Container WIDTH. 
            // CSS % on 'height' is relative to Container HEIGHT.
            // This makes 'auto' logic tricky if we want to use % for position.
            // Position 'left' % is relative to Container Width. 'top' % relative to Container Height.

            // If we use 'transform: translate(-X%, -Y%)', it is relative to the ELEMENT (Image) size.
            // This is KEY.
            // x is normalized start.
            // We want to shift left by x * ImageWidth.
            // ImageWidth is 100% of Element Width.
            // So shift is x * 100 %.

            // Let's verify:
            // We want crop start x to be at container 0.
            // Current position of crop start x in element is x * ElementWidth.
            // If we translate by -(x * ElementWidth), that pixel moves to 0. Is this 0 of the element or container?
            // It moves the element left.
            // If element was at 0,0, then crop start x is at x*W.
            // TranslateX(-x*W) moves the element so that crop start x is at 0.
            // Correct.

            // So using transform is UNIVERSAL regardless of scaling logic!
            style.transform = `translate(${-x * 100}%, ${-y * 100}%)`;

            // But we still need to set the dimensions correctly so 'Cover' works.
            // Above: style.height = `${100 / h}%`; style.width = 'auto';
            // This ensures Crop Height fills Container Height.

            // BUT wait, is 'width: auto' going to result in the correct AR?
            // Only if the container itself has the aspect ratio of the image? NO.
            // The browser calculates width based on the image's intrinsic ratio and the set height.
            // So the Element will have the correct AR.
            // Its height will be 100/h % of container.
            // So Crop Section matches Container Height.
            // Since Crop is WIDER (AR > TargetAR), the Crop Width will be > Container Width.
            // So we cover width too. 
            // Loop closed.

        } else {
            // Crop is taller than target - scale to fit Width
            // We want CropWidthCSS = 100% (of Container Width).
            // w * W_css = 100%
            // W_css = 100 / w %

            style.width = `${100 / w}%`;
            style.height = 'auto';
            style.transform = `translate(${-x * 100}%, ${-y * 100}%)`;
        }

        return style;
    }, [x, y, w, h, imageWidth, imageHeight, configWidth, configHeight]); // Removed srcAspect from deps

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', ...style }}>
            <Img src={src} style={imgStyle} />
           
        </div>
    );
};

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
